import { useNavigate } from "@tanstack/react-router";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  LogOut,
  Settings,
  Check,
  Home,
  ChevronRight,
  MessageSquare,
  Users,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";
import { trpc } from "@/lib/trpc";
import { authClient } from "@/lib/auth";

interface MobileMenuSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileMenuSheet({ open, onOpenChange }: MobileMenuSheetProps) {
  const navigate = useNavigate();
  const { data: user } = trpc.user.me.useQuery();
  const { selectedCommunityId, setSelectedCommunityId } = useCommunity();
  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();

  const getInitials = (name?: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatUnit = (
    unit: { unitNumber: string; blockSection: string | null } | null,
  ) => {
    if (!unit) return null;
    if (unit.blockSection) {
      return `${unit.blockSection} - ${unit.unitNumber}`;
    }
    return unit.unitNumber;
  };

  const currentCommunity = communities?.find(
    (c) => c.id === selectedCommunityId,
  );
  const isSecurityGuard = currentCommunity?.role === "SECURITY";
  const isCommunityAdmin = currentCommunity?.role === "COMMUNITY_ADMIN";

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 md:hidden"
        onClick={() => onOpenChange(false)}
      />

      {/* Menu card */}
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-4 duration-200 md:hidden">
        <div className="overflow-hidden rounded-2xl border bg-card shadow-xl">
          {/* User info with sign out */}
          <div className="flex items-center gap-3 p-4">
            <Avatar className="h-11 w-11">
              <AvatarImage
                src={user?.image || undefined}
                alt={user?.name || "Usuario"}
              />
              <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 overflow-hidden">
              <p className="truncate font-medium">{user?.name || "Usuario"}</p>
              <p className="truncate text-sm text-muted-foreground">
                {user?.email || ""}
              </p>
            </div>
            <button
              onClick={async () => {
                await authClient.signOut();
                navigate({ to: "/login" });
              }}
              className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="Cerrar sesión"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>

          <Separator />

          {/* Communities with unit info */}
          {communities && communities.length > 0 && (
            <>
              <div className="py-1">
                {communities.map((community) => {
                  const unitLabel = formatUnit(community.unit);
                  const isSelected = community.id === selectedCommunityId;

                  return (
                    <button
                      key={community.id}
                      onClick={() => {
                        setSelectedCommunityId(community.id);
                        onOpenChange(false);
                      }}
                      className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-accent rounded-xl mx-0"
                    >
                      <div className="flex items-center gap-3">
                        {unitLabel && (
                          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-muted">
                            <Home className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className={isSelected ? "font-medium" : ""}>
                            {community.name}
                          </p>
                          {unitLabel && (
                            <p className="text-sm text-muted-foreground">
                              {unitLabel}
                            </p>
                          )}
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })}
              </div>
              <Separator />
            </>
          )}

          {/* Role-specific links + Settings */}
          <div className="py-1">
            {!isSecurityGuard && (
              <button
                onClick={() => {
                  navigate({ to: "/tickets" });
                  onOpenChange(false);
                }}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-accent"
              >
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span>Tickets</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            {isCommunityAdmin && (
              <button
                onClick={() => {
                  navigate({ to: "/members" });
                  onOpenChange(false);
                }}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-accent"
              >
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>Miembros</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            <button
              onClick={() => {
                navigate({ to: "/settings" });
                onOpenChange(false);
              }}
              className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-accent"
            >
              <div className="flex items-center gap-3">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span>Configuración</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
